import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Auth.css';
import { api } from '../../utils/api';
import API_BASE from '../../config';
import logo from '../../assets/logo.png';

function Login({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const navigate = useNavigate();

  // Clear any stale auth state when login page mounts
  useEffect(() => {
    localStorage.removeItem('token');
  }, []);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setError('Too many login attempts. Please wait a few minutes and try again.');
        } else {
          setError(data.detail || 'Invalid credentials');
        }
        return;
      }

      if (data.requires2fa) {
        setRequires2FA(true);
        setInfo('We sent a 6-digit code to your email. Enter it below.');
        setCode('');
        return;
      }

      localStorage.setItem('token', data.access_token);

      if (onLoginSuccess) onLoginSuccess();
      navigate('/dashboard');
    } catch (err) {
      setError(
        'Unable to connect to the server. Please try a hard refresh (Ctrl+Shift+R or Cmd+Shift+R) and try again. ' +
        'If the problem persists, check your internet connection.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (code.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }

    setLoading(true);

    try {
      const verifyResponse = await api.verify2FA(email, code);
      localStorage.setItem('token', verifyResponse.access_token);
      
      if (onLoginSuccess) onLoginSuccess();
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setLoading(true);

    try {
      await api.resend2FA(email);
      setInfo('New code sent. Check your inbox and spam folder.');
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setCode('');
    setError('');
    setInfo('');
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-header">
          <div className="auth-logo-div">
            <img src={logo} alt="Kigitz Logo" />
          </div>
          <p>{requires2FA ? 'Enter verification code' : 'Sign in to your dashboard'}</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {info && <div className="info-message">{info}</div>}

        {!requires2FA ? (
          <form onSubmit={handleCredentialsSubmit}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="auth-link-center">
              <Link to="/forgot-password" className="auth-link">
                Forgot your password?
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <div className="form-group">
              <label>Verification Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                disabled={loading}
                className="code-input"
              />
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>

            <div className="two-fa-actions">
              <button type="button" onClick={handleBack} className="btn-secondary" disabled={loading}>
                Back
              </button>
              <button type="button" onClick={handleResend} className="btn-secondary" disabled={loading}>
                Resend Code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;